<template>
  <div
    class="adsense-slot"
    :class="[
      `adsense-slot--${placement}`,
      { 'adsense-slot--collapsed': isCollapsed },
    ]"
    :data-ad-name="adName"
    :data-ad-slot="adSlot"
    :data-ad-state="adState"
    aria-label="Advertisement"
  >
    <ins
      ref="adElement"
      class="adsbygoogle adsense-slot__ins"
      style="display:block"
      :data-ad-client="client"
      :data-ad-slot="adSlot"
      data-ad-format="auto"
      data-full-width-responsive="true"
    ></ins>
  </div>
</template>

<script>
export default {
  name: 'AdSenseSlot',
  props: {
    adName: {
      type: String,
      required: true,
    },
    adSlot: {
      type: String,
      required: true,
    },
    placement: {
      type: String,
      default: 'inline',
    },
    client: {
      type: String,
      default: 'ca-pub-5921166401404543',
    },
  },
  data() {
    return {
      adState: 'idle',
      isCollapsed: false,
      loadObserver: null,
      statusObserver: null,
      retryTimer: null,
      retryCount: 0,
    }
  },
  mounted() {
    this.observeViewport()
  },
  beforeDestroy() {
    this.clearObservers()
    clearTimeout(this.retryTimer)
  },
  methods: {
    observeViewport() {
      if (typeof window === 'undefined') return
      if (!window.IntersectionObserver) {
        this.loadAd()
        return
      }
      this.loadObserver = new window.IntersectionObserver(this.onIntersect, {
        rootMargin: '300px 0px',
        threshold: 0,
      })
      this.loadObserver.observe(this.$el)
    },
    onIntersect(entries) {
      if (!entries.some(entry => entry.isIntersecting)) return
      this.loadAd()
      this.disconnectLoadObserver()
    },
    loadAd() {
      if (this.adState !== 'idle') return
      this.adState = 'loading'
      this.observeStatus()
      this.pushAd()
    },
    pushAd() {
      if (typeof window === 'undefined') return
      try {
        (window.adsbygoogle = window.adsbygoogle || []).push({})
        this.adState = 'pushed'
      } catch (error) {
        this.scheduleRetry(error)
      }
    },
    scheduleRetry(error) {
      this.retryCount += 1
      if (this.retryCount > 4) {
        this.adState = 'push-error'
        console.error(`AdSense push failed for ${this.adName}:`, error)
        return
      }
      this.retryTimer = setTimeout(() => this.pushAd(), 500)
    },
    observeStatus() {
      if (typeof window === 'undefined' || !window.MutationObserver) return
      const element = this.$refs.adElement
      if (!element) return
      this.statusObserver = new window.MutationObserver(this.syncStatus)
      this.statusObserver.observe(element, {
        attributes: true,
        attributeFilter: ['data-ad-status', 'adsbygoogle-status'],
      })
    },
    syncStatus() {
      const element = this.$refs.adElement
      if (!element) return
      const status = element.getAttribute('data-ad-status')
      if (status) this.adState = status
      this.isCollapsed = status === 'unfilled' || status === 'error'
    },
    disconnectLoadObserver() {
      if (!this.loadObserver) return
      this.loadObserver.disconnect()
      this.loadObserver = null
    },
    clearObservers() {
      this.disconnectLoadObserver()
      if (!this.statusObserver) return
      this.statusObserver.disconnect()
      this.statusObserver = null
    },
  },
}
</script>

<style lang="scss" scoped>
.adsense-slot {
  width: 100%;
  max-width: $container-max-width;
  margin: 0 auto;
  padding: 0 rem(24);

  &--home-top {
    padding-top: rem(16);
  }

  &--home-news {
    padding-top: rem(8);
    padding-bottom: rem(4);
  }

  &--collapsed {
    display: none;
  }

  &__ins {
    width: 100%;
    min-height: rem(72);
    border-radius: rem(6);
    overflow: hidden;
    background: rgba(255, 255, 255, 0.02);

    @include pc {
      min-height: rem(90);
    }
  }
}
</style>
